<template>
  <div>
    <div class="mb-56 flex items-center">
      <div class="flex flex-1 flex-col">
        <h2 class="heading-2 mb-8">
          {{ i18n(translations.manageUserPermissions) }}
        </h2>
        <div class="body-1">
          {{ i18n(translations.addNewUserInfoText) }}
        </div>
      </div>
      <RevButton variant="primary" @click="handleOpenUserForm(undefined)">
        {{ i18n(translations.addNewUser) }}
      </RevButton>
    </div>
    <h3 class="body-1-bold mb-24">
      {{ i18n(translations.numberOfUsers, { count: users.length }) }}
    </h3>
    <RevCard
      v-for="user in users"
      :key="user.id"
      class="mb-16 flex items-center p-20"
    >
      <div class="flex-1 flex-col">
        <RevTag
          class="mb-8"
          :label="getUserStateLabel(user)"
          :variant="getUserStateVariant(user)"
        />
        <div class="mb-8">
          <div class="body-1-bold">
            {{
              i18n(translations.fullName, {
                firstName: user.firstName,
                lastName: user.lastName,
              })
            }}
          </div>
          <div v-if="shouldDisplayNamePronunciation(user)">
            {{
              i18n(translations.fullName, {
                firstName: user.firstNamePronunciation,
                lastName: user.lastNamePronunciation,
              })
            }}
          </div>
        </div>
        <div class="body-2">{{ user.email }}</div>
      </div>
      <RevButton
        v-if="isUserActive(user)"
        size="small"
        variant="secondary"
        @click="handleOpenUserForm(user)"
      >
        {{ i18n(translations.edit) }}
      </RevButton>
      <RevButton
        v-if="canToggleState(user)"
        class="ml-8"
        size="small"
        variant="secondary"
        @click="handleToggleUserState(user)"
      >
        {{ getToggleUserStateLabel(user) }}
      </RevButton>
    </RevCard>
    <RevCard v-if="!hasUsers">
      <RevContentBlock
        class="px-56 py-16"
        :image-props="{
          alt: i18n(translations.noResultsDescription),
          height: 257,
          src: '/img/empty.svg',
          width: 465,
        }"
        :surtitle="i18n(translations.noResultsDescription)"
        :title="i18n(translations.noResultsTitle)"
      />
    </RevCard>
    <UserStateModal
      v-bind="stateModalData"
      @user-updated="handleUserStateUpdated"
    />
    <UserFormDrawer
      :user="userToEdit"
      @user-created="handleUserCreated"
      @user-edited="handleUserUpdated"
    />
  </div>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { computed, reactive, ref } from 'vue'

import { sellerUsersAPI } from '@backmarket/http-api'
import type {
  SellerUser,
  SellerUserStateCode,
} from '@backmarket/http-api/src/api-specs-seller-backoffice/seller-users'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevContentBlock } from '@ds/components/ContentBlock'
import { openModal } from '@ds/components/ModalBase'
import { RevTag } from '@ds/components/Tag'

import { MODAL_NAMES } from '../../Profile.constants'

import { USER_STATE_CODES } from './UserPermissions.constants'
import translations from './UserPermissions.translations'
import UserFormDrawer from './components/UserFormDrawer/UserFormDrawer.vue'
import UserStateModal from './components/UserStateModal/UserStateModal.vue'

const i18n = useI18n()
const { openErrorToast } = useTheToast()

const { data, error, refresh } = await useHttpFetch(
  sellerUsersAPI.getSellerUsers,
)

if (error.value) {
  openErrorToast({
    content: getApiErrorMessage(error.value as unknown as Error),
  })
}

const stateModalData = reactive<{
  userId: SellerUser['id']
  stateCode: SellerUserStateCode
}>({
  userId: 0,
  stateCode: USER_STATE_CODES.ACTIVATED,
})

const userToEdit = ref<SellerUser>()

const users = computed(() => data.value?.results || [])

const hasUsers = computed(() => users.value?.length)

function shouldDisplayNamePronunciation(user: SellerUser) {
  return !!user.firstNamePronunciation && !!user.lastNamePronunciation
}

function getUserStateVariant(user: SellerUser) {
  if (user.isAdmin) {
    return 'alternate'
  }

  return user.state.code === USER_STATE_CODES.ACTIVATED ? 'success' : 'danger'
}

function canToggleState(user: SellerUser) {
  return !user.isAdmin
}

function isUserActive(user: SellerUser) {
  return user.state.code !== USER_STATE_CODES.DEACTIVATED
}

function getUserStateLabel(user: SellerUser) {
  return user.isAdmin ? i18n(translations.admin) : user.state.label
}

function getToggleUserStateLabel(user: SellerUser) {
  return i18n(
    isUserActive(user) ? translations.delete : translations.reactivate,
  )
}

function handleToggleUserState(user: SellerUser) {
  stateModalData.stateCode =
    user.state.code === USER_STATE_CODES.ACTIVATED
      ? USER_STATE_CODES.DEACTIVATED
      : USER_STATE_CODES.ACTIVATED
  stateModalData.userId = user.id
  openModal(MODAL_NAMES.USER_PERMISSIONS.ACTIVATE_OR_DEACTIVATE_USER)
}

function handleUserStateUpdated() {
  refresh()
}

function handleUserCreated() {
  refresh()
}

function handleUserUpdated() {
  refresh()
}

function handleOpenUserForm(user?: SellerUser) {
  userToEdit.value = user
  openModal(MODAL_NAMES.USER_PERMISSIONS.USER_FORM)
}
</script>
